/**
 * Authentication API — POST /api/automation/Login/Login
 * OTP verification — POST /api/automation/Login/VerifyOtp — body: { userName, otp }
 */

import { apiClient } from '@/services/api.service';
import type { ApiResponse } from '@/types/common.types';

function getBool(obj: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = obj[k];
    if (v === true || v === 1) return true;
    if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
  }
  return false;
}

function getFirst<T>(obj: Record<string, unknown>, keys: string[]): T | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null) return v as T;
  }
  return undefined;
}

/** Some backends wrap the payload in data/result. */
function normalizeLoginPayload(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const looksLikeLogin = (x: Record<string, unknown>) =>
    'success' in x ||
    'Success' in x ||
    'isOtp' in x ||
    'IsOtp' in x ||
    'isOTP' in x ||
    'otpVerification' in x ||
    'OtpVerification' in x;

  if (looksLikeLogin(o)) return o;
  for (const key of ['data', 'Data', 'result', 'Result', 'value', 'Value']) {
    const inner = o[key];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const innerObj = inner as Record<string, unknown>;
      if (looksLikeLogin(innerObj)) return innerObj;
    }
  }
  return o;
}

/** Only `success` / `Success` — do not use `isSuccess` (some APIs send isSuccess:false with a successful OTP verify). */
function readSuccessFlag(body: Record<string, unknown>): boolean | undefined {
  const v = getFirst(body, ['success', 'Success']);
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
  }
  return undefined;
}

/** VerifyOtp: infer outcome when `success` is missing but `message` describes the result. */
function readVerifyOtpOutcome(body: Record<string, unknown>): boolean | undefined {
  const flag = readSuccessFlag(body);
  if (flag !== undefined) return flag;
  const msg = getFirst<string>(body, ['message', 'Message']);
  if (!msg?.trim()) return undefined;
  const m = msg.toLowerCase();
  if (m.includes('verified') && (m.includes('success') || m.includes('otp'))) return true;
  if (
    m.includes('invalid') ||
    m.includes('wrong') ||
    m.includes('incorrect') ||
    m.includes('expired') ||
    m.includes('mismatch')
  ) {
    return false;
  }
  return undefined;
}

function loginFailureMessage(res: ApiResponse<unknown>): string {
  const err = (res.error ?? '').trim();
  const low = err.toLowerCase();

  if (
    low.includes('failed to fetch') ||
    low.includes('networkerror') ||
    low.includes('load failed') ||
    low.includes('network request failed')
  ) {
    return 'Cannot reach the login server. Use the dev proxy (/api-backend in next.config) or enable CORS on the API, or set NEXT_PUBLIC_API_BASE_URL correctly.';
  }
  if (low.includes('timeout') || low.includes('timed out') || res.status === 408) {
    return 'Request timed out. Check that the backend is running.';
  }
  if (res.status === 401 || res.status === 403) {
    return err || 'Username or password is wrong';
  }
  if (err) return err;
  return 'Username or password is wrong';
}

function needsOtpFromBody(body: Record<string, unknown>): boolean {
  return (
    getBool(body, 'isOtp', 'IsOtp', 'isOTP') ||
    getBool(body, 'otpVerification', 'OtpVerification')
  );
}

export interface LoginApiUser {
  userID?: number;
  userName?: string;
}

export type LoginWithPasswordResult =
  | { ok: false; error: string }
  | {
    ok: true;
    needsOtp: true;
    message?: string;
    user?: LoginApiUser;
  }
  | {
    ok: true;
    needsOtp: false;
    message?: string;
    token?: string;
    user?: LoginApiUser;
  };

/**
 * POST automation/Login/Login — body: { userName, password }
 */
export async function loginWithPassword(userName: string, password: string): Promise<LoginWithPasswordResult> {
  const res = await apiClient.post<unknown>('automation/Login/Login', {
    userName,
    password,
  });

  if (!res.success) {
    return { ok: false, error: loginFailureMessage(res) };
  }

  const body = normalizeLoginPayload(res.data);
  if (!body) {
    return { ok: false, error: 'Invalid response from server. Please try again.' };
  }

  const apiSuccess = readSuccessFlag(body);
  if (apiSuccess === false) {
    const msg = getFirst<string>(body, ['message', 'Message']);
    return {
      ok: false,
      error: msg && msg.trim() ? msg.trim() : 'Username or password is wrong',
    };
  }

  const needsOtp = needsOtpFromBody(body);
  // const needsOtp = false;

  if (needsOtp) {
    const userRaw = getFirst<Record<string, unknown>>(body, ['user', 'User']);
    const user: LoginApiUser | undefined = userRaw
      ? {
        userID: getFirst<number>(userRaw, ['userID', 'UserID', 'userId', 'UserId']),
        userName: getFirst<string>(userRaw, ['userName', 'UserName']) ?? userName,
      }
      : { userName };

    return {
      ok: true,
      needsOtp: true,
      message: getFirst<string>(body, ['message', 'Message']),
      user,
    };
  }

  const token =
    getFirst<string>(body, ['token', 'Token', 'accessToken', 'AccessToken', 'jwt', 'Jwt']) ?? undefined;

  const userRaw = getFirst<Record<string, unknown>>(body, ['user', 'User']);
  const user: LoginApiUser | undefined = userRaw
    ? {
      userID: getFirst<number>(userRaw, ['userID', 'UserID', 'userId', 'UserId']),
      userName: getFirst<string>(userRaw, ['userName', 'UserName']) ?? userName,
    }
    : { userName };

  return {
    ok: true,
    needsOtp: false,
    message: getFirst<string>(body, ['message', 'Message']),
    token,
    user,
  };
}

export type VerifyOtpResult =
  | { ok: false; error: string }
  | { ok: true; token?: string; user?: LoginApiUser };

/**
 * POST automation/Login/VerifyOtp — body: { userName, otp } (Swagger)
 * On success, response may include user: null; session still completes using userName.
 */
export async function verifyLoginOtp(userName: string, otp: string): Promise<VerifyOtpResult> {
  const code = otp.trim().replace(/\s+/g, '');
  const res = await apiClient.post<unknown>('automation/Login/VerifyOtp', {
    userName,
    otp: code,
    // Some .NET model binders expect PascalCase; harmless if ignored.
    UserName: userName,
    Otp: code,
  });

  if (!res.success) {
    const err = (res.error ?? '').trim();
    if (err) return { ok: false, error: err };
    return { ok: false, error: 'Could not verify OTP. Check your connection and try again.' };
  }

  const data = normalizeLoginPayload(res.data);
  if (!data) {
    return { ok: false, error: 'Invalid response from server. Please try again.' };
  }

  const outcome = readVerifyOtpOutcome(data);
  if (outcome === false) {
    const msg = getFirst<string>(data, ['message', 'Message']);
    return { ok: false, error: msg && msg.trim() ? msg.trim() : 'Invalid OTP' };
  }

  const token =
    getFirst<string>(data, ['token', 'Token', 'accessToken', 'AccessToken', 'jwt', 'Jwt']) ?? undefined;

  const userVal = data.user ?? data.User;
  const userRaw =
    userVal != null && typeof userVal === 'object'
      ? (userVal as Record<string, unknown>)
      : undefined;
  const user: LoginApiUser | undefined = userRaw
    ? {
      userID: getFirst<number>(userRaw, ['userID', 'UserID', 'userId', 'UserId']),
      userName: getFirst<string>(userRaw, ['userName', 'UserName']) ?? userName,
    }
    : { userName };

  return { ok: true, token, user };
}

export type CheckSessionResult =
  | { ok: false }
  | { ok: true };

/**
 * Check if the current session is valid via API
 * GET automation/Login/CheckSession
 * Returns simple true/false indicating session validity
 * Backend handles session identification (cookies/headers)
 */
export async function checkSession(): Promise<CheckSessionResult> {
  const res = await apiClient.get<unknown>('automation/Login/CheckSession');

  if (!res.success) {
    return { ok: false };
  }

  const data = normalizeLoginPayload(res.data);
  if (!data) {
    return { ok: false };
  }

  const apiSuccess = readSuccessFlag(data);
  if (apiSuccess === false) {
    return { ok: false };
  }

  // API returns true/false - if we got here with success, session is valid
  return { ok: true };
}

/**
 * Logout and clear session on backend
 * POST automation/Login/Logout
 */
export async function logoutSession(): Promise<void> {
  try {
    await apiClient.post<unknown>('automation/Login/Logout', {});
  } catch {
    // Ignore errors - logout on frontend anyway
  }
}
